/**
 * Let a customer choose a shoe size in their own numbers.
 *
 * Not a conversion table printed underneath — the size buttons themselves are
 * relabelled. Pick EU 42 and you have selected the variant the shop calls
 * UK 8; the basket, the order and the warehouse all still say UK 8, because
 * only the text on the button changes. The input's value is never touched, so
 * nothing downstream has to know this happened.
 *
 * Which system is showing when the page opens comes from the market the
 * visitor is already browsing, and their own choice is remembered after that.
 *
 * A size the brand has published no equivalent for shows its own label rather
 * than a blank or a guess.
 */
(function () {
  'use strict';

  var STORE = 'tc_shoe_system';
  var EU_COUNTRIES = ['DE','FR','IT','ES','NL','BE','AT','PT','IE','PL','SE','DK','FI','CZ','GR','RO','HU','SK','BG','HR','SI','LT','LV','EE','LU','CY','MT'];

  function localSystem(country) {
    if (country === 'US' || country === 'CA') return 'us';
    if (EU_COUNTRIES.indexOf(country) !== -1) return 'eu';
    return 'uk';
  }

  /*
   * Which tab opens.
   *
   * The customer's own choice first. Then the system their market thinks in —
   * but only if the brand has published it for every size in stock. New Rock
   * is why: their chart starts at EU 39, so a British visitor sent to the UK
   * tab would find the three smallest boots still reading 36, 37, 38 and the
   * row looking half-translated. Where the local system cannot cover the
   * ladder, the brand's own is the honest default, and New Rock is sold in EU.
   */
  function preferred(country, sizes, systems) {
    var saved = null;
    try { saved = localStorage.getItem(STORE); } catch (e) { /* private mode */ }
    if (saved && systems.indexOf(saved) !== -1) return saved;

    var local = localSystem(country);
    var complete = systems.indexOf(local) !== -1 &&
      sizes.every(function (s) { return s[local] !== null || s[local + 'Text']; });
    if (complete) return local;

    var native = (sizes[0] && sizes[0].system) || local;
    return systems.indexOf(native) !== -1 ? native : systems[0];
  }

  function init(root) {
    var handle = root.getAttribute('data-handle');
    var api = (root.getAttribute('data-endpoint') || '').replace(/\/$/, '');
    var country = (root.getAttribute('data-country') || 'GB').toUpperCase();
    if (!handle || !api) return;

    fetch(api + '/products/' + encodeURIComponent(handle) + '/shoe-sizes')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.sizes || !data.sizes.length) return;
        if (!data.systems || data.systems.length < 2) return;
        mount(data);
      })
      .catch(function () { /* the shop's own labels are already on screen */ });

    function mount(data) {
      // The option group whose values these sizes are.
      var byLabel = {};
      data.sizes.forEach(function (s) { byLabel[s.label] = s; });

      var group = null;
      var inputs = [].slice.call(document.querySelectorAll('.option-selector .js-option'));
      for (var i = 0; i < inputs.length; i++) {
        if (byLabel[inputs[i].value]) { group = inputs[i].closest('.option-selector'); break; }
      }
      if (!group) return;

      var active = preferred(country, data.sizes, data.systems);

      var strip = document.createElement('div');
      strip.className = 'tc-shoe-tabs';
      strip.setAttribute('role', 'group');
      strip.setAttribute('aria-label', 'Show sizes in');

      function relabel() {
        [].slice.call(group.querySelectorAll('.js-option')).forEach(function (input) {
          var row = byLabel[input.value];
          if (!row) return;
          var span = (document.querySelector('label[for="' + input.id + '"]') || input.closest('label') || input)
            .querySelector('.js-value');
          if (!span) return;
          if (!span.hasAttribute('data-shop-label')) span.setAttribute('data-shop-label', span.textContent.trim());
          /* The brand's own wording wins where it gave one: Pikolinos maps an
             EU 40 to "6-6.5", and narrowing that to 6 would be us deciding
             something the maker deliberately left open. */
          var shown = row[active + 'Text'] || row[active];
          /* The system is written on the button, not only on the tab above
             it. A row of bare numbers is ambiguous the moment somebody
             screenshots it or reads it out on the phone — "a 42" means two
             different feet depending on where you are standing. */
          span.textContent = shown === null || shown === undefined
            ? span.getAttribute('data-shop-label')
            : active.toUpperCase() + ' ' + shown;
          input.setAttribute('aria-label', span.getAttribute('data-shop-label') +
            (shown === null || shown === undefined ? '' : ' (' + active.toUpperCase() + ' ' + shown + ')'));
        });
      }

      function draw() {
        strip.innerHTML = '';
        data.systems.forEach(function (sys) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'tc-shoe-tab';
          b.textContent = sys.toUpperCase();
          b.setAttribute('aria-pressed', sys === active ? 'true' : 'false');
          b.addEventListener('click', function () {
            active = sys;
            try { localStorage.setItem(STORE, sys); } catch (e) { /* private mode */ }
            draw();
            relabel();
          });
          strip.appendChild(b);
        });
      }

      /* Read as one thing: the tabs sit on the top edge of a panel holding
         the sizes, so switching system is obviously switching what is inside
         it rather than a stray control floating above a row of buttons. */
      var btns = group.querySelector('.option-selector__btns') ||
                 (group.querySelector('.js-option') || {}).parentNode;
      if (btns && btns.parentNode) {
        var panel = document.createElement('div');
        panel.className = 'tc-shoe-panel';
        btns.parentNode.insertBefore(panel, btns);
        panel.appendChild(strip);
        var body = document.createElement('div');
        body.className = 'tc-shoe-panel__body';
        panel.appendChild(body);
        body.appendChild(btns);
      } else {
        group.insertBefore(strip, group.firstChild);
      }

      draw();
      relabel();
      // The theme rewrites this row when the colour changes; put our numbers back.
      document.addEventListener('change', function () { setTimeout(relabel, 40); });
    }
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-tc-shoe]')).forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
